import { getCustomerAuthFromCookies } from '@bulknutrients/bulk-shared-lib/lib/api/auth';
import { getCustomerId } from '@bulknutrients/bulk-shared-lib/lib/api/customer';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { LoginForm } from '@components/LoginForm';
import { RewardsLink } from '@components/Typography/TextLink/TextLink';
import type { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { type ReactElement, useState } from 'react';
import { Button } from '../components/Button';
import { Container } from '../components/Container';
import { AccountHomePageSkeleton } from './account/home';

// Component
function CreateAccount() {
  return (
    <div className="bg-grey-light p-5 dark:bg-grey-darkest">
      <h2 className="italic">Create new account</h2>
      <p>Create an account with Bulk Nutrients and you'll be able to:</p>
      <ul className="my-4 list-disc pl-10">
        <li className="mb-0.5 text-sm">Add favourite products</li>
        <li className="mb-0.5 text-sm">
          Earn{' '}
          <RewardsLink data-testid="whey-cool-rewards-link">
            Whey Cool Points
          </RewardsLink>
        </li>
        <li className="mb-0.5 text-sm">Checkout faster</li>
        <li className="mb-0.5 text-sm">Save multiple shipping addresses</li>
        <li className="mb-0.5 text-sm">Access your order history</li>
        <li className="mb-0.5 text-sm">Track new orders</li>
      </ul>
      <Button href="/create-account" data-testid="create-account-link">
        Create Account
      </Button>
    </div>
  );
}

export default function LoginPage(): ReactElement {
  const router = useRouter();
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    router.push('/account');
    return <AccountHomePageSkeleton />;
  }

  return (
    <>
      <Head>
        <title>Bulk Nutrients create or sign into an existing account</title>
        <meta
          name="title"
          content="Bulk Nutrients create or sign into an existing account"
        />
        <meta
          name="description"
          content="Log into your existing Bulk Nutrients account and access previous orders, favourites, saved shipping addresses , Whey Cool loyalty points and more"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/login`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Login', path: '/login' },
          ]}
        />
      </Container>
      {/** biome-ignore lint/correctness/useUniqueElementIds: we just the main-content with the skip to content button */}
      <Container as="main" id="main-content">
        <div className="mb-5 grid grid-cols-1 gap-[75px] sm:grid-cols-2 sm:gap-4">
          <div>
            <h1>Sign in</h1>
            <LoginForm onLogin={() => setSubmitted(true)} />
          </div>
          <CreateAccount />
        </div>
      </Container>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  // Get customer id if available
  const { cookies } = req;
  const customerAuth = getCustomerAuthFromCookies(cookies);
  const customerId = customerAuth && (await getCustomerId(customerAuth));

  // If customer, redirect to account home
  if (customerId) {
    return {
      redirect: {
        destination: '/account/home',
        permanent: false,
      },
    };
  }

  // return empty props
  return { props: {} };
};

LoginPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};
